"use client";

import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import { axisLabelMaxWidth } from "./axis-label-budget";

/**
 * Width the y-axis rail holds before the axis has rendered a single label, so
 * the plot does not jump sideways on the first measured frame.
 */
export const CHART_Y_AXIS_RAIL_MIN_WIDTH = 40;

/**
 * Box that holds one row of x-axis tick labels. The rail reuses it for the
 * x-axis caption, so caption and ticks sit on the same text baseline.
 */
export const CHART_X_AXIS_ROW_CLASS = "flex min-h-8 w-full items-start pt-2";

const CAPTION_CLASS =
  "inline-block text-chart-label text-xs leading-tight [overflow-wrap:anywhere]";

interface ChartAxisSlotsValue {
  /** Layout column left of the horizontal scroller. Null until mounted. */
  yRail: HTMLDivElement | null;
  /** Layout row below the plot, inside the scroller. Null until mounted. */
  xRail: HTMLDivElement | null;
  registerXRail: (node: HTMLDivElement | null) => void;
  /** Author-set caption. Wins over anything an axis derives from its ticks. */
  xAxisLabel: string | null;
  registerDerivedXAxisLabel: (label: string | null) => void;
}

const ChartAxisSlotsContext = createContext<ChartAxisSlotsValue | null>(null);

/** Null outside a {@link ChartAxisFrame} — axes then fall back to overlay mode. */
export function useChartAxisSlots(): ChartAxisSlotsValue | null {
  return useContext(ChartAxisSlotsContext);
}

/**
 * Flex-row margins that drop fixed-width label boxes onto exact x centres. The
 * first box is held at the row's left edge so it cannot be scrolled out of
 * reach; every later box keeps its exact centre.
 */
export function axisLabelFlowOffsets(
  centers: number[],
  boxWidth: number
): number[] {
  let cursor = 0;
  return centers.map((center, index) => {
    const desired = center - boxWidth / 2;
    const left = index === 0 ? Math.max(0, desired) : desired;
    const offset = left - cursor;
    cursor = left + boxWidth;
    return offset;
  });
}

function AxisCaption({ text }: { text: string }) {
  return (
    <span
      className={CAPTION_CLASS}
      style={{ maxWidth: axisLabelMaxWidth("axisName") }}
    >
      {text}
    </span>
  );
}

export interface ChartAxisFrameProps {
  children: ReactNode;
  className?: string;
  railMinWidth?: number;
  /** Caption parked in the rail next to the x-axis tick row. */
  xAxisLabel?: string;
  /** Caption parked in the rail above the topmost y-axis tick. */
  yAxisLabel?: string;
}

/**
 * Row that holds a chart's y-axis outside the horizontal scroller. The rail is
 * a flex sibling of the scroller, so it never moves and needs no scroll
 * listener; its width comes from the widest label the axis renders into it.
 *
 * The axis captions live in that same rail: the x one on the tick row's line,
 * the y one above the first tick. Both widen the rail through normal intrinsic
 * sizing, which is what pushes the plot to the right.
 */
export function ChartAxisFrame({
  children,
  className,
  railMinWidth = CHART_Y_AXIS_RAIL_MIN_WIDTH,
  xAxisLabel,
  yAxisLabel,
}: ChartAxisFrameProps) {
  const [yRail, setYRail] = useState<HTMLDivElement | null>(null);
  const [xRail, setXRail] = useState<HTMLDivElement | null>(null);
  const [derivedXAxisLabel, setDerivedXAxisLabel] = useState<string | null>(
    null
  );

  const authoredXCaption = xAxisLabel?.trim() || null;
  const yCaption = yAxisLabel?.trim() || null;

  const value = useMemo<ChartAxisSlotsValue>(
    () => ({
      yRail,
      xRail,
      registerXRail: setXRail,
      xAxisLabel: authoredXCaption,
      registerDerivedXAxisLabel: setDerivedXAxisLabel,
    }),
    [yRail, xRail, authoredXCaption]
  );

  const xCaption = authoredXCaption ?? derivedXAxisLabel;

  return (
    <ChartAxisSlotsContext.Provider value={value}>
      <div className={cn("flex w-full items-stretch", className)}>
        <div
          className="relative flex shrink-0 flex-col"
          style={{ minWidth: railMinWidth }}
        >
          {yCaption && (
            <div className="pr-2 text-right">
              <AxisCaption text={yCaption} />
            </div>
          )}
          {/* The dashed edge belongs on the rail itself: the axis portals a
              zero-height measuring stack plus absolutely placed tick rows, so a
              border set in there has no height to draw along. */}
          <div
            className="relative flex-1 border-r border-dashed border-[var(--border)]"
            ref={setYRail}
          />
          {xCaption && (
            <>
              {/* The scroll port carries its own bottom padding and the rail
                  stretches over it, so the caption box is inset by the same
                  amount to land on the tick row. */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 pb-1">
                <div
                  className={cn(
                    CHART_X_AXIS_ROW_CLASS,
                    "justify-end pr-2 text-right"
                  )}
                >
                  <AxisCaption text={xCaption} />
                </div>
              </div>
              {/* The visible box above is absolute and cannot widen the rail. */}
              <div
                aria-hidden="true"
                className="invisible h-0 overflow-hidden pr-2 text-right"
              >
                <AxisCaption text={xCaption} />
              </div>
            </>
          )}
        </div>
        {yCaption ? (
          <div className="min-w-0 flex-1">
            {/* Holds the plot down by exactly the y caption's height, so the
                tick rail and the plot keep the same top edge. */}
            <div aria-hidden="true" className="invisible">
              <AxisCaption text={yCaption} />
            </div>
            {children}
          </div>
        ) : (
          children
        )}
      </div>
    </ChartAxisSlotsContext.Provider>
  );
}

/**
 * Flow row for the x-axis labels. Belongs directly below the plot and inside
 * the scroller, so the labels scroll with the plot and their height is part of
 * the module's height instead of sitting on top of the chart.
 */
export function ChartXAxisRow({ className }: { className?: string }) {
  const slots = useChartAxisSlots();
  if (!slots) {
    return null;
  }
  return (
    <div className={cn("relative w-full", className)} ref={slots.registerXRail} />
  );
}
